import { type SearchConfluenceSubtype } from '../../../../common/types';
import { type AggResponse } from '../../../../common/utils/fetch-agg';

// Types for AGG graphQL queries are NOT autogenerated in CFE
// This file was manually created
export enum ActivityProduct {
	JIRA = 'jira',
	CONFLUENCE = 'confluence',
	ATLAS = 'townsquare',
	GRAPH = 'graph',
	ASSETS = 'assets',
	GITHUB = 'github',
	LOOM = 'loom',
}

export enum VIEWED_EVENT {
	VIEWED = 'viewed',
	SL_VIEWED = 'sl-viewed',
}

export enum WORKED_EVENT {
	COMMENTED = 'commented',
	CREATED = 'created',
	EDITED = 'edited',
	PUBLISHED = 'published',
	UPDATED = 'updated',
}

export type ActivityEventType = VIEWED_EVENT | WORKED_EVENT;

export interface Ancestor {
	id: string;
	title: string | null;
}

export interface Link {
	base: string | null;
	webUi: string | null;
}

export interface JiraProjectField {
	project: {
		name: string;
	} | null;
}

export interface JiraIssueTypeField {
	issueType: {
		name: string;
	} | null;
}

export interface JiraIssue {
	projectField: JiraProjectField | null;
	issueTypeField: JiraIssueTypeField | null;
}

export interface JiraFieldNode {
	text: string | null;
	issue: JiraIssue | null;
}

export interface JiraFieldEdge {
	node: JiraFieldNode | null;
}
export interface QuickFindAllActivityDataEdgeObjectDataStatusCategoryNode {
	name: string;
	colorName: string;
}

export interface FieldsById {
	edges: JiraFieldEdge[] | null;
}

export interface Space {
	id: string | null;
	name: string | null;
}

export interface IssueType {
	avatar?: {
		small?: string | null;
	} | null;
}

export interface QuickFind1PEntity {
	title?: string | null;
	key?: string;
	name?: string;
	pageStatus?: string;
	blogStatus?: string;
	subtype?: SearchConfluenceSubtype;
	ancestors?: Ancestor[] | null;
	links?: Link | null;
	url?: string | null;
	webUrl?: string;
	iconData?: string | null;
	issueType?: IssueType | null;
	fieldsById?: FieldsById | null;
	space?: Space | null;
	statusCategory?: QuickFindAllActivityDataEdgeObjectDataStatusCategoryNode;
}

export interface ExternalDocument {
	id?: string;
	displayName?: string;
	type?: ExternalDocumentType;
	url?: string | null;
	parent?: ExternalDocument;
	provider?: ExternalProvider;
}

export interface ExternalRemoteLink {
	id?: string;
	displayName?: string;
	url?: string | null;
	provider?: ExternalProvider;
	category?: string;
}

enum ExternalDocumentCategory {
	DOCUMENT = 'DOCUMENT',
	PRESENTATION = 'PRESENTATION',
	SPREADSHEET = 'SPREADSHEET',
}

export interface ExternalDocumentType {
	category?: ExternalDocumentCategory;
}
export interface ExternalProvider {
	providerId?: string;
}

export interface LoomVideo {
	id?: string;
	name?: string;
	/** Aliased as `loomUrl` in the GraphQL fragment to avoid nullability conflicts with other fragments' `url` fields. Use `loomUrl` to access the video URL. */
	loomUrl?: string;
	owner?: {
		name?: string | null;
	} | null;
	playableDuration?: number | null;
}

export type QuickFindLoomEntity = LoomVideo;

export interface ExternalPullRequest {
	id?: string;
	displayName?: string;
	url?: string | null;
	status?: string | null;
	provider?: {
		providerId?: string;
	};
}

export type QuickFindGraphPullRequestEntity = ExternalPullRequest;

export interface ExternalWorkItem {
	id?: string;
	displayName?: string;
	url?: string | null;
	workItemStatus?: string | null;
	provider?: {
		providerId?: string;
	};
}

export type QuickFindGraphWorkItemEntity = ExternalWorkItem;

export type ActivityEntity =
	| QuickFind1PEntity
	| QuickFindGraphEntity
	| QuickFindAssetsEntity
	| QuickFindGithubEntity
	| QuickFindLoomEntity;

export type QuickFindGraphEntity =
	| ExternalDocument
	| ExternalRemoteLink
	| ExternalPullRequest
	| ExternalWorkItem;

export interface QuickFindGithubEntity {
	id?: string;
	displayName?: string;
	url?: string | null;
}

export interface ActivityObject<EntityType = ActivityEntity> {
	id: string;
	type: string;
	product: ActivityProduct;
	data: EntityType | null;
}

export interface ActivityEvent {
	eventType: ActivityEventType;
	timestamp: string;
}

export interface ActivityNode<EntityType = ActivityEntity> {
	object: ActivityObject<EntityType>;
	event: ActivityEvent;
}

export interface ActivityItemEdge {
	node: ActivityNode;
	cursor: string;
}

export interface ActivityConnection {
	edges: ActivityItemEdge[];
}

export interface QuickFindAllActivity {
	all: ActivityConnection;
}

export interface QuickFindMyActivity {
	myActivity: QuickFindAllActivity | null;
}

export interface QuickFindRecentActivitiesQuery {
	activity: QuickFindMyActivity | null;
}

export interface QuickFindRecentActivitiesQueryVariables {
	filteredProducts: string[];
	filteredObjectTypes: string[];
	filteredEventTypes: string[];
	itemsLimit: number;
	filteredRootContainerIds: string[];
	after?: string;
	includeJiraIssueMetadata?: boolean;
	includeLoomVideoMetadata?: boolean;
}

export type QuickFindRecentActivitiesResponse = AggResponse<QuickFindRecentActivitiesQuery>;

export interface JiraIssuesQueryVariables {
	jira_ids: string[];
}

export interface JiraIssueById {
	id: string;
	summary?: string;
	descriptionField?: {
		description?: string;
		richText?: {
			adfValue?: {
				convertedPlainText?: {
					plainText?: string;
				};
			};
		};
	};
}

export interface JiraIssueResponse {
	jira_issuesByIds?: JiraIssueById[];
}

export const isRemoteLinkActivity = (
	activity: ActivityObject<QuickFindGraphEntity>,
): activity is ActivityObject<ExternalRemoteLink> => {
	return activity.type === 'remote-link';
};

export const isDocumentActivity = (
	activity: ActivityObject<QuickFindGraphEntity>,
): activity is ActivityObject<ExternalDocument> => {
	return activity.type === 'document';
};

export const isGraphActivity = (
	activity: ActivityObject<ActivityEntity>,
): activity is ActivityObject<QuickFindGraphEntity> => {
	return activity.product === 'graph';
};

export const isGithubActivity = (
	activity: ActivityObject<ActivityEntity>,
): activity is ActivityObject<QuickFindGraphEntity> => {
	return activity.product === 'github';
};

export const is1PActivity = (
	activity: ActivityObject<ActivityEntity>,
): activity is ActivityObject<QuickFind1PEntity> => {
	return (
		activity.product === 'confluence' ||
		activity.product === 'jira' ||
		activity.product === 'townsquare'
	);
};

export interface AssetsLink extends Link {
	web?: string | null;
}

export interface AssetsIcon {
	url48?: string | null;
}

export type QuickFindAssetsEntity = {
	id?: string;
	displayTypename?: string;
	links?: AssetsLink | null;
	assetsLabel?: string;
	assetsDisplayName?: string;
	objectKey?: string;
	assetsIcon?: AssetsIcon | null;
	objectType?: {
		schema?: {
			name?: string | null;
			assetsSchemaDisplayName?: string | null;
		} | null;
	} | null;
	schema?: {
		name?: string | null;
		assetsSchemaName?: string | null;
		assetsSchemaDisplayName?: string | null;
	} | null;
};

export const isAssetsActivity = (
	activity: ActivityObject<ActivityEntity>,
): activity is ActivityObject<QuickFindAssetsEntity> => {
	return activity.product === ActivityProduct.ASSETS;
};

export const isLoomActivity = (
	activity: ActivityObject<ActivityEntity>,
): activity is ActivityObject<QuickFindLoomEntity> => {
	return activity.product === ActivityProduct.LOOM;
};

export const isGraphPullRequestActivity = (
	activity: ActivityObject<ActivityEntity>,
): activity is ActivityObject<QuickFindGraphPullRequestEntity> => {
	return activity.product === ActivityProduct.GRAPH && activity.type === 'pull-request';
};

export const isGraphWorkItemActivity = (
	activity: ActivityObject<ActivityEntity>,
): activity is ActivityObject<QuickFindGraphWorkItemEntity> => {
	return activity.product === ActivityProduct.GRAPH && activity.type === 'work-item';
};
